import type { Attachment } from '../../store/previewer/types'

import {
  CloseOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Image, Spin, Table } from 'antd'
import { useEffect, useRef, useState } from 'react'
import UTIF from 'utif2'

import $api from '../../API'
import { useAppDispatch, useAppSelector } from '../../store'
import * as previewActions from '../../store/previewer'
import hljs from 'highlight.js/lib/core'
import xml from 'highlight.js/lib/languages/xml'

hljs.registerLanguage('xml', xml)

const MAX_FILE_SIZE_FOR_PREVIEW = 25 * 1024 * 1024

export const Previewer = (): JSX.Element | null => {
  const { isOpen, attachment } = useAppSelector(state => state.previewer)

  const dispatch = useAppDispatch()

  const sizeQuery = useQuery({
    queryKey: ['file-size', attachment?.href],
    queryFn: async () => {
      if (!attachment?.href) {
        return null
      }

      try {
        const response = await $api.head(attachment.href)
        const contentLength = response.headers['content-length']

        return contentLength ? Number.parseInt(contentLength, 10) : null
      } catch {
        return null
      }
    },
    enabled: isOpen && !!attachment?.href,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    staleTime: Infinity
  })

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && isOpen) {
        dispatch(previewActions.close())
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, dispatch])

  if (!isOpen || sizeQuery.isLoading) {
    return null
  }

  const handleCloseClick = (): void => {
    dispatch(previewActions.close())
  }

  if (!attachment || !attachment.href) {
    return (
      <Container isOpen={isOpen} onClose={handleCloseClick}>
        <div className='w-full h-full bg-[#282828]'>
          <div className='flex justify-center items-center w-full h-full'>
            <div className='flex flex-col gap-4 items-center p-8 text-center'>
              <FileTextOutlined className='text-6xl text-gray-400' />

              <div className='space-y-2'>
                <h3 className='text-xl font-medium text-gray-300'>No preview available</h3>

                <p className='text-gray-400'>Invalid file URL</p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    )
  }

  if (sizeQuery.data && sizeQuery.data > MAX_FILE_SIZE_FOR_PREVIEW) {
    return (
      <Container isOpen={isOpen} onClose={handleCloseClick} attachment={attachment}>
        <div className='w-full h-full bg-[#282828]'>
          <div className='flex justify-center items-center w-full h-full'>
            <div className='flex flex-col gap-4 items-center p-8 text-center'>
              <FileTextOutlined className='text-6xl text-gray-400' />

              <div className='space-y-2'>
                <h3 className='text-xl font-medium text-gray-300'>File too large to preview</h3>

                <p className='text-gray-400'>
                  This file is larger than 25MB and cannot be previewed in the browser.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    )
  }

  if (attachment?.mimeType === 'application/pdf') {
    return <PDFViewer isOpen={isOpen} onClose={handleCloseClick} attachment={attachment} />
  }

  if (attachment?.mimeType === 'text/csv') {
    return <CSVViewer isOpen={isOpen} onClose={handleCloseClick} attachment={attachment} />
  }

  if (attachment?.mimeType === 'text/plain') {
    return <TextViewer isOpen={isOpen} onClose={handleCloseClick} attachment={attachment} />
  }

  if (attachment?.mimeType === 'image/tiff') {
    return <TiffViewer isOpen={isOpen} onClose={handleCloseClick} attachment={attachment} />
  }

  if (attachment?.mimeType === 'text/xml') {
    return <XmlViewer isOpen={isOpen} onClose={handleCloseClick} attachment={attachment} />
  }

  return (
    <Container isOpen={isOpen} onClose={handleCloseClick} attachment={attachment}>
      <div className='w-full h-full bg-[#282828]'>
        <div className='flex justify-center items-center w-full h-full'>
          <div className='flex flex-col gap-4 items-center p-8 text-center'>
            <FileTextOutlined className='text-6xl text-gray-400' />

            <div className='space-y-2'>
              <h3 className='text-xl font-medium text-gray-300'>No preview available</h3>

              <p className='text-gray-400'>
                This file type &quot;{attachment?.mimeType}&quot; cannot be previewed in the
                browser.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}

interface ViewerProps {
  isOpen: boolean
  onClose: () => void
  attachment?: Attachment
}

const PDFViewer = (props: ViewerProps): JSX.Element | null => {
  const previousAttachmentRef = useRef<string | null>(null)
  const previousBlobUrlRef = useRef<string | null>(null)

  const query = useQuery({
    queryKey: ['preview', props.attachment?.href],

    async queryFn({ queryKey }) {
      const [, attachmentHref] = queryKey

      if (attachmentHref === previousAttachmentRef.current && previousBlobUrlRef.current) {
        return previousBlobUrlRef.current
      }

      if (attachmentHref) {
        const response = await $api.get(attachmentHref, {
          responseType: 'blob'
        })

        const blob = new Blob([response.data], { type: props.attachment?.mimeType })
        const blobUrl = URL.createObjectURL(blob)

        previousAttachmentRef.current = attachmentHref
        previousBlobUrlRef.current = blobUrl

        return blobUrl
      }

      return null
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    staleTime: Infinity,

    enabled:
      props.attachment?.href !== undefined &&
      props.attachment?.href !== previousAttachmentRef.current
  })

  if (!props.isOpen) {
    return null
  }

  if (query.isLoading) {
    return <LoadingState attachment={props.attachment} />
  }

  if (!query.data) {
    return <EmptyState {...props} />
  }

  return (
    <Container {...props}>
      <div className='w-full h-screen'>
        <embed
          src={query.data}
          title={props.attachment?.name ? String(props.attachment.name) : undefined}
          type={props.attachment?.mimeType ?? 'application/pdf'}
          className='w-full h-full'
        />
      </div>
    </Container>
  )
}

const TextViewer = (props: ViewerProps): JSX.Element | null => {
  const query = useQuery({
    queryKey: ['preview', props.attachment?.href],

    async queryFn({ queryKey }) {
      const [, attachmentHref] = queryKey

      if (attachmentHref) {
        const response = await $api.get(
          attachmentHref.replace('localhost:3333', 'dev-payments.lucenthealth.com'),
          {
            responseType: 'blob'
          }
        )

        const blob = new Blob([response.data], { type: props.attachment?.mimeType })

        return blob.text()
      }

      return null
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    staleTime: Infinity,

    enabled: props.attachment?.href !== undefined
  })

  if (!props.isOpen) {
    return null
  }

  if (query.isLoading) {
    return <LoadingState attachment={props.attachment} />
  }

  if (!query.data) {
    return <EmptyState {...props} />
  }

  return (
    <Container {...props}>
      <div className='overflow-auto w-full h-screen bg-[#282828]'>
        <div className='flex justify-center w-full h-full'>
          <div className='pb-12 w-[56rem] min-h-full'>
            <pre className='overflow-x-auto p-4 h-full text-sm leading-relaxed text-gray-800 bg-white'>
              {query.data}
            </pre>
          </div>
        </div>
      </div>
    </Container>
  )
}

const CSVViewer = (props: ViewerProps): JSX.Element | null => {
  const query = useQuery({
    queryKey: ['preview', props.attachment?.href],

    async queryFn({ queryKey }) {
      const [, attachmentHref] = queryKey

      if (attachmentHref) {
        const response = await $api.get(
          attachmentHref.replace('localhost:3333', 'dev-payments.lucenthealth.com'),
          {
            responseType: 'blob'
          }
        )

        const blob = new Blob([response.data], { type: props.attachment?.mimeType })
        const text = await blob.text()

        // Parse CSV content
        const lines = text.trim().split('\n')
        if (lines.length === 0 || !lines[0]) {
          return null
        }

        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''))
        const rows = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''))
          const row: Record<string, string> = {}
          headers.forEach((header, index) => {
            row[header] = values[index] || ''
          })
          return row
        })

        return { headers, rows }
      }

      return null
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    staleTime: Infinity,

    enabled: props.attachment?.href !== undefined
  })

  if (!props.isOpen) {
    return null
  }

  if (query.isLoading) {
    return <LoadingState attachment={props.attachment} />
  }

  if (!query.data) {
    return <EmptyState {...props} />
  }

  const { headers, rows } = query.data

  const columns = headers.map(header => ({
    title: header,
    dataIndex: header,
    key: header,
    width: 150,
    render: (text: string) => (
      <div className='text-xs' title={text}>
        {text}
      </div>
    )
  }))

  return (
    <Container {...props}>
      <div className='overflow-auto w-full h-screen bg-[#282828]'>
        <div className='p-4 w-full h-full'>
          <div className='overflow-hidden bg-white rounded-lg shadow-lg'>
            <div className='p-4 bg-gray-50 border-b'>
              <h3 className='text-lg font-semibold text-gray-800'>CSV Data ({rows.length} rows)</h3>
            </div>
            <div className='overflow-auto max-h-[calc(100vh-200px)]'>
              <Table
                dataSource={rows}
                columns={columns}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows`
                }}
                size='small'
                rowKey={(record, index) => index?.toString() || '0'}
                scroll={{ x: headers.length * 150 }}
                className='csv-table'
              />
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}

const TiffViewer = (props: ViewerProps): JSX.Element | null => {
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)

  const query = useQuery({
    queryKey: ['preview', { href: props.attachment?.href }],

    async queryFn({ queryKey }) {
      const { href } = queryKey[1] as typeof queryKey extends Array<string | infer T> ? T : never

      const response = await $api.get(href!)

      const images = Array.isArray(response.data) ? response.data : [response.data]

      const result = []

      for (let i = 0; i < images.length; i += 1) {
        const checkBuffer = new Uint8Array([...atob(images[i].data)].map(c => c.charCodeAt(0)))
          .buffer

        const ifd = UTIF.decode(checkBuffer)[0] as UTIF.IFD

        UTIF.decodeImage(checkBuffer, ifd)

        const rgba = UTIF.toRGBA8(ifd)

        const myCanvas = document.createElement('canvas')
        myCanvas.width = ifd.width
        myCanvas.height = ifd.height

        const ctx = myCanvas.getContext('2d')

        if (ctx === null) {
          throw new Error('Could not load canvas 2d context')
        }

        const imageData = ctx.createImageData(myCanvas.width, myCanvas.height)

        for (let i = 0; i < rgba.length; i++) {
          imageData.data[i] = rgba[i] as number
        }

        ctx.putImageData(imageData, 0, 0)

        const url = myCanvas.toDataURL('image/png')

        result.push(url)
      }

      return result
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    staleTime: Infinity,

    enabled: props.attachment?.href !== undefined
  })

  useEffect(() => {
    if (query.data && query.data.length > 0 && props.isOpen) {
      setPreviewVisible(true)
      setPreviewIndex(0)
    }
  }, [query.data, props.isOpen])

  if (!props.isOpen) {
    return null
  }

  if (query.isLoading) {
    return <LoadingState attachment={props.attachment} />
  }

  if (!query.data || query.data.length === 0) {
    return <EmptyState {...props} />
  }

  return (
    <div className='hidden'>
      <Image.PreviewGroup
        preview={{
          minScale: 0.4,
          scaleStep: 0.2,
          maskClosable: false,
          visible: previewVisible,
          current: previewIndex,
          onVisibleChange: visible => {
            setPreviewVisible(visible)
            if (!visible) {
              props.onClose()
            }
          },
          onChange: current => {
            setPreviewIndex(current)
          }
        }}
      >
        {query.data.map((imageSource, index) => (
          <Image key={index} src={imageSource} />
        ))}
      </Image.PreviewGroup>
    </div>
  )
}

const XmlViewer = (props: ViewerProps): JSX.Element | null => {
  const query = useQuery({
    queryKey: ['preview', { href: props.attachment?.href }],

    async queryFn({ queryKey }) {
      const { href } = queryKey[1] as typeof queryKey extends Array<string | infer T> ? T : never

      if (href) {
        const response = await $api.get(href!)

        return atob(response.data.data)
      }

      return null
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    staleTime: Infinity,

    enabled: props.attachment?.href !== undefined
  })

  if (!props.isOpen) {
    return null
  }

  if (query.isLoading) {
    return <LoadingState attachment={props.attachment} />
  }

  if (!query.data) {
    return <EmptyState {...props} />
  }

  const highlightedXml = hljs.highlight(query.data, {
    language: 'xml'
  }).value

  return (
    <Container {...props}>
      <div className='overflow-auto w-full h-screen bg-[#282828]'>
        <div className='flex justify-center w-full h-full'>
          <div className='pb-12 w-[56rem] min-h-full'>
            <pre
              className='overflow-x-auto p-4 h-full text-sm leading-relaxed text-gray-800 bg-white'
              dangerouslySetInnerHTML={{ __html: highlightedXml }}
            />
          </div>
        </div>
      </div>
    </Container>
  )
}

const LoadingState = (props: Pick<ViewerProps, 'attachment'>): JSX.Element => {
  const textsMapper = {
    'text/plain': 'Loading Text Files...',
    'text/csv': 'Loading CSV Files...',
    'application/pdf': 'Loading PDF Files...',
    'image/tiff': 'Loading TIFF Files...',
    'text/xml': 'Loading XML Files...'
  }

  const loadingText =
    textsMapper[props.attachment?.mimeType as keyof typeof textsMapper] ?? textsMapper['text/plain']

  return (
    <div className='flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50'>
      <div className='flex flex-col gap-4 justify-center items-center p-8 bg-white rounded-lg shadow-lg'>
        <Spin spinning size='large' />

        <p className='text-lg font-medium text-gray-700'>{loadingText}</p>
      </div>
    </div>
  )
}

const EmptyState = (props: ViewerProps): JSX.Element => {
  const handleCloseClick = (): void => {
    props.onClose()
  }

  return (
    <div className='flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50'>
      <div className='flex flex-col justify-center items-center p-8 max-w-md bg-white rounded-lg shadow-lg'>
        <p className='mb-4 text-lg text-center text-gray-600 font-inter'>
          Something went wrong while loading the preview. Please try again later.
        </p>
        <Button type='primary' onClick={handleCloseClick}>
          Close
        </Button>
      </div>
    </div>
  )
}

interface ContainerProps extends ViewerProps {
  children: React.ReactNode
}

const Container = (props: ContainerProps): JSX.Element => {
  const iconsMapper = {
    'text/plain': FileTextOutlined,
    'text/csv': FileTextOutlined,
    'text/xml': FileTextOutlined,
    'application/pdf': FilePdfOutlined,
    'image/tiff': FileImageOutlined
  }

  const Icon =
    iconsMapper[props.attachment?.mimeType as keyof typeof iconsMapper] ?? iconsMapper['text/plain']

  const handleCloseClick = (): void => {
    props.onClose()
  }

  return (
    <div className='fixed inset-0 z-50 bg-[#282828]'>
      <div className='flex justify-between items-center px-4 py-2 bg-[#3C3C3C]'>
        <div className='flex gap-4 justify-center items-center'>
          <div className='flex justify-center items-center p-2 rounded-full'>
            <Icon className='text-white' />
          </div>

          <h2 className='text-xl font-semibold text-white truncate'>{props.attachment?.name}</h2>
        </div>

        <button
          className='flex justify-center items-center p-2 rounded-full hover:bg-white hover:bg-opacity-10'
          onClick={handleCloseClick}
        >
          <CloseOutlined className='text-sm text-white' />
        </button>
      </div>

      {props.children}
    </div>
  )
}
